import type { AdminStayListItem, AdminStayStatus } from '@/api/core/stays';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { PropertyCell } from '@/components/copy-id-button';
import {
  clearListParams,
  ListFilterToolbar,
  patchListParams,
} from '@/components/list-filter-toolbar';
import { ListNote, TABLE_BODY_CLASS } from '@/components/list-note';
import { usePropertyDirectory } from '@/components/property-filter';
import { RowLink } from '@/components/row-link';
import { displayStayStatus, StayPeriod, StayStatusBadge } from '@/pages/stays/stay-presentation';
import {
  Button,
  DataTable,
  ListPage,
  PageHeader,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router';
import { hasStayReadAccess, stayListQuery, stayRuntime, type StayRuntime } from './stay-queries';
import { stayDetailHref, stayListState } from './stay-url';

const statuses: readonly AdminStayStatus[] = [
  'PREPARING',
  'READY',
  'CHECKED_IN',
  'CHECKED_OUT',
  'CANCELLED',
  'NO_SHOW',
  'TERMINATED',
];
const options = [...statuses.map((value) => ({ value, label: displayStayStatus(value) }))];

const FILTER_KEYS = {
  propertyId: 'property_id',
  status: 'status',
  periodStart: 'starts_at',
  periodEnd: 'ends_at',
};

export function StayListPage({
  runtime = stayRuntime,
  enableServerSorting = false,
}: {
  runtime?: StayRuntime;
  enableServerSorting?: boolean;
}) {
  const session = useOperatorSession(runtime.session);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const state = stayListState(params);
  const invalid = !state.valid || (!enableServerSorting && state.sortBy !== null);
  const allowed = hasStayReadAccess(runtime.session, session.generation);
  const list = useQuery(
    stayListQuery(
      runtime,
      session.generation,
      {
        cursor: state.cursor,
        propertyId: state.propertyId ?? undefined,
        status: state.status ?? undefined,
        startsAt: state.startsAt ?? undefined,
        endsAt: state.endsAt ?? undefined,
        sortBy: state.sortBy ?? undefined,
        sortDirection: state.sortDirection ?? undefined,
      },
      state.valid && allowed && (enableServerSorting || state.sortBy === null),
    ),
  );
  const directory = usePropertyDirectory(runtime);
  const hasFilters = [state.propertyId, state.status, state.startsAt, state.endsAt].some(Boolean);
  const nextCursor = list.isError ? null : (list.data?.nextCursor ?? null);
  const columns: DataTableColumn<AdminStayListItem>[] = [
    {
      key: 'stay_id',
      sortKey: 'stay_id',
      sortable: enableServerSorting,
      header: 'Stay ID',
      cell: (row) => (
        <RowLink to={stayDetailHref(row.stay_id, location.pathname + location.search)}>
          <span className="break-all">{row.stay_id}</span>
        </RowLink>
      ),
    },
    {
      key: 'status',
      sortKey: 'status',
      sortable: enableServerSorting,
      header: '상태',
      headerClassName: 'w-[88px]',
      cellClassName: 'w-[88px] whitespace-normal',
      cell: (row) => <StayStatusBadge status={row.status} />,
    },
    {
      key: 'start_at',
      sortKey: 'start_at',
      sortable: enableServerSorting,
      header: '투숙 기간',
      cell: (row) => <StayPeriod startAt={row.start_at} endAt={row.end_at} inline />,
    },
    {
      key: 'property_id',
      sortable: false,
      header: '지점',
      cell: (row) => (
        <PropertyCell id={row.property_id} name={directory.names?.get(row.property_id)} />
      ),
    },
  ];
  if (session.status !== 'authorized') return null;
  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title="투숙 현황"
          description="지점별 투숙의 저장된 상태와 기간을 조회합니다."
          className="items-start sm:items-center"
        />
      </ShellHeader>
      {allowed ? (
        invalid ? (
          <div className="p-6" role="alert">
            <p>필터 주소가 올바르지 않습니다.</p>
            <Button
              nativeButton={false}
              role="link"
              variant="outline"
              render={<Link to="/stays" />}
            >
              필터 초기화
            </Button>
          </div>
        ) : (
          <>
            <ListFilterToolbar
              directory={directory}
              propertyId={state.propertyId}
              status={state.status}
              statusLabel="Stay 상태"
              statusOptions={options}
              periodStart={state.startsAt}
              periodEnd={state.endsAt}
              periodPlaceholder="조회 기간"
              isFetching={list.isFetching && !list.isPending}
              onChange={(patch) => {
                setParams(patchListParams(params, patch, FILTER_KEYS));
              }}
              onReset={() => {
                setParams(clearListParams(params, FILTER_KEYS));
              }}
            />
            <ListNote>
              저장된 값 기준이며 실제 객실 점유나 출입 준비 상태를 보장하지 않습니다. 기간은 현재
              기기 시간대의 날짜 단위로 고르고, 겹치는 투숙을 한 번에 최대 20건씩 보여 줍니다.
              {enableServerSorting && ' 투숙 기간 열은 시작 시각 기준으로 정렬됩니다.'}
            </ListNote>
            <ListPage.Body className="border-t">
              {list.isError ? (
                <div role="alert" className="flex flex-col items-start gap-2 p-4">
                  <p>
                    {isApiError(list.error) && list.error.status === 403
                      ? '현재 Booking 조회 권한이 없습니다.'
                      : isApiError(list.error) && list.error.status === 404
                        ? '투숙 현황 목록을 찾을 수 없습니다.'
                        : isApiError(list.error) && list.error.kind === 'invalid-response'
                          ? '투숙 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.'
                          : '투숙 현황을 불러오지 못했습니다. 연결 상태를 확인해 주세요.'}
                  </p>
                  <Button variant="outline" onClick={() => void list.refetch()}>
                    다시 조회
                  </Button>
                </div>
              ) : (
                <div className={TABLE_BODY_CLASS}>
                  <DataTable
                    columns={columns}
                    data={list.data?.items ?? []}
                    sort={
                      enableServerSorting && state.sortBy && state.sortDirection
                        ? `${state.sortBy},${state.sortDirection}`
                        : null
                    }
                    onSortChange={
                      enableServerSorting
                        ? (sort) => {
                            const next = new URLSearchParams(params);
                            next.delete('cursor');
                            if (sort) {
                              const [sortBy, sortDirection] = sort.split(',');
                              next.set('sort_by', sortBy ?? '');
                              next.set('sort_direction', sortDirection ?? '');
                            } else {
                              next.delete('sort_by');
                              next.delete('sort_direction');
                            }
                            setParams(next);
                          }
                        : undefined
                    }
                    rowKey={(row) => row.stay_id}
                    isLoading={list.isPending && list.fetchStatus === 'fetching'}
                    empty={
                      hasFilters
                        ? {
                            title: '조건에 맞는 투숙이 없습니다',
                            description: '지점·상태·기간 조건을 바꾸거나 조건을 초기화해 보세요.',
                          }
                        : {
                            title: '표시할 투숙이 없습니다',
                            description: '지점이나 기간을 골라 조회 범위를 좁힐 수 있습니다.',
                          }
                    }
                  />
                </div>
              )}
            </ListPage.Body>
            <ListPage.Pagination>
              <div className="flex flex-wrap items-center gap-3">
                {state.cursor && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      next.delete('cursor');
                      setParams(next);
                    }}
                  >
                    처음으로
                  </Button>
                )}
                {nextCursor !== null && (
                  <Button
                    disabled={list.isFetching}
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      next.set('cursor', nextCursor);
                      setParams(next);
                    }}
                  >
                    다음 20건
                  </Button>
                )}
              </div>
            </ListPage.Pagination>
          </>
        )
      ) : (
        <div className="p-6" role="alert">
          현재 Booking 조회 권한이 없습니다. 투숙 현황을 표시하지 않습니다.
        </div>
      )}
    </ListPage>
  );
}
